/**
 * "Install app" support. Android Chrome (and desktop Chrome/Edge) offer a one-tap install once the page
 * qualifies; the offer arrives as a `beforeinstallprompt` event, kept here until the person taps Install.
 * iPhone Safari has no such event, so there the app shows the Share → Add to Home Screen steps instead.
 */
type PromptEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let offer: PromptEvent | null = null;
const listeners = new Set<() => void>();
const changed = () => { for (const f of listeners) f(); };

addEventListener('beforeinstallprompt', e => { e.preventDefault(); offer = e as PromptEvent; changed(); });
addEventListener('appinstalled', () => { offer = null; changed(); });

export const onInstallChange = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };

/** Already opened from the home screen. */
export const installed = () => matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
const ios = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** 'prompt': one-tap install is available. 'ios': show the Add to Home Screen steps. null: nothing to offer. */
export function installKind(): 'prompt' | 'ios' | null {
  if (installed()) return null;
  if (offer) return 'prompt';
  return ios() ? 'ios' : null;
}

export async function install(): Promise<boolean> {
  if (!offer) return false;
  const e = offer;
  await e.prompt();
  const { outcome } = await e.userChoice;
  offer = null; changed();
  return outcome === 'accepted';
}

const KEY = 'nf-install-dismissed';
export const dismissed = () => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } };
export const dismiss = () => { try { localStorage.setItem(KEY, '1'); } catch { /* ignore */ } changed(); };
